import { useState } from "react";
import type { TSortOrder } from "types";

type ColumnBenef = {
    id:
        | "name"
        | "grossDuties"
        | "rightsGross"
        | "totalContributions"
        | "netDuties"
        | "paidStatus"
        | "grossQuantities"
        | "returnsQuantities"
        | "netQuantities"
        | "initialInventory"
        | "printedInventory"
        | "totalScrappedInventory"
        | "totalPressInventory"
        | "theoricSales"
        | "control"
        | "creationDate"
        | "beneficiar"
        | "title"
        | "totalAmount"
        | "date"
        | "grossValue"
        | "netValue"
        | "totalValue"
        | "amount"
        | "note"
        | "target"
        | "numberOfLines"
        | "user"
        | "filename"
        | "netRemainingToPay"
        | "bankInfoComplete"
        | "netToPay"
        | "grossDuty"
        | "contributions"
        | "period"
        | "type";
};

function usePagination<T extends Record<string, any>>(
    filteredArray: T[],
    customSort?: (array: T[], orderBy: ColumnBenef["id"], orderAsc: TSortOrder) => T[],
): [
    T[],
    (event: unknown, newPage: number) => void,
    (event: React.ChangeEvent<HTMLInputElement>) => void,
    number,
    number,
    number,
    React.Dispatch<React.SetStateAction<number>>,
    TSortOrder,
    ColumnBenef["id"],
    (id: ColumnBenef["id"]) => (event: React.MouseEvent<unknown>) => void,
] {
    const localSettings = parseInt(localStorage.getItem("settings_pagination") ?? "10");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(localSettings);
    const [beginPagination, setBeginPagination] = useState(0);
    const [orderBy, setOrderBy] = useState("" as ColumnBenef["id"]);
    const [orderAsc, setOrderAsc] = useState("asc" as TSortOrder);

    const handleChangePage = (event: unknown, newPage: number) => {
        setBeginPagination(newPage * rowsPerPage);
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPage(Math.floor((beginPagination + +event.target.value - 1) / +event.target.value));
        setRowsPerPage(+event.target.value);
        localStorage.setItem("settings_pagination", event.target.value);
    };

    const handleSort = (id: ColumnBenef["id"]) => (event: React.MouseEvent<unknown>) => {
        setOrderBy(id);
        setOrderAsc(orderAsc === "asc" ? "desc" : "asc");
    };

    const dividedByPage =
        filteredArray && filteredArray.length !== 0 && customSort == undefined
            ? [...filteredArray]
                  .sort((a, b) => {
                      if (typeof a[orderBy] === "number") {
                          if (+a[orderBy] - +b[orderBy] > 0) {
                              return orderAsc === "asc" ? 1 : -1;
                          } else {
                              return orderAsc === "asc" ? -1 : 1;
                          }
                      }
                      if (typeof a[orderBy] === "string") {
                          if (a[orderBy].localeCompare(b[orderBy]) > 0) {
                              return orderAsc === "asc" ? 1 : -1;
                          } else {
                              return orderAsc === "asc" ? -1 : 1;
                          }
                      }
                      return 0;
                  })
                  .slice(beginPagination, beginPagination + rowsPerPage)
            : filteredArray && filteredArray.length !== 0 && customSort != undefined
              ? customSort([...filteredArray], orderBy, orderAsc).slice(beginPagination, beginPagination + rowsPerPage)
              : filteredArray;

    return [
        dividedByPage,
        handleChangePage,
        handleChangeRowsPerPage,
        page,
        rowsPerPage,
        beginPagination,
        setPage,
        orderAsc,
        orderBy,
        handleSort,
    ];
}
export default usePagination;
